/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import uniqueId from 'lodash/uniqueId';

import {TButtonAppearance, TButtonSize} from '../Button';
import {FluidOverlay} from '../Dropdown';
import {Stack} from '../primitives';
import {TPlacementX, TPlacementY} from '../types';
import * as S from './style';

type TActionButtonOption = {
  id?: string | number;
  content: React.ReactNode;
  handler?: TAnyFunction;
  disabled?: boolean;
  options?: TActionButtonOption[];
  props?: TAnyObject;
};

export type TButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  className?: string;
  square?: boolean;
  appearance?: TButtonAppearance;
  size?: TButtonSize;
};

export type TProps = TButtonProps & {
  options: TActionButtonOption[];
  children: React.ReactNode;
  overlayPlacementX?: TPlacementX;
  overlayPlacementY?: TPlacementY;
  overlayClassName?: string;
};

type TInnerActionButtonOption = TActionButtonOption & {
  id: number | string;
  options?: TInnerActionButtonOption[];
};

type TOptionsProps = {
  options: TInnerActionButtonOption[];
  size: TButtonSize;
  placementX: TPlacementX;
  placementY: TPlacementY;
  overlayClassName?: string;
  onSelect: TEmptyFunction;
};

const DEFAULT_OFFSET = 4;

const Options = React.memo<TOptionsProps>(
  ({onSelect, options, size, placementY, placementX, overlayClassName}) => {
    const [selectedOption, selectOption] = React.useState<number | string | null>(null);
    const [hoveredOption, hoverOption] = React.useState<number | string | null>(null);

    const handleClick = React.useCallback(
      (item: TInnerActionButtonOption) => (event: React.MouseEvent) => {
        item.handler?.(item.id, event);

        if (!item.options?.length) {
          onSelect();
        }
      },
      [onSelect],
    );

    return (
      <div onMouseLeave={() => selectOption(null)}>
        {options.map((item, index) => (
          <React.Fragment key={item.id}>
            <S.Option
              isDisabled={item.disabled}
              isHovered={hoveredOption === item.id}
              onMouseEnter={() => {
                hoverOption(item.id);
                selectOption(!item.disabled && item.options?.length ? item.id : null);
              }}
              onMouseLeave={() => hoverOption(null)}
              onClick={handleClick(item)}
              size={size}
              isFirst={index === 0}
              isLast={index === options.length - 1}
              {...item.props}
            >
              <S.OptionContent size={size}>{item.content}</S.OptionContent>
              {!!item.options?.length && (
                <>
                  <S.ArrowIcon size={size} />
                  <FluidOverlay
                    isOpen={selectedOption === item.id}
                    placementX={placementX}
                    placementY={placementY}
                    offsetX={DEFAULT_OFFSET}
                    overlayComponent={S.NestedOverlay}
                    className={overlayClassName}
                    onMouseEnter={() => hoverOption(null)}
                    onMouseLeave={() => hoverOption(item.id)}
                  >
                    <Options
                      options={item.options}
                      size={size}
                      placementX={placementX}
                      placementY={placementY}
                      onSelect={onSelect}
                      overlayClassName={overlayClassName}
                    />
                  </FluidOverlay>
                </>
              )}
            </S.Option>
          </React.Fragment>
        ))}
      </div>
    );
  },
);

Options.displayName = 'Options';

const mapOptionsWithId = (options: TActionButtonOption[]): TInnerActionButtonOption[] =>
  options?.map((option) => ({
    ...option,
    options: option.options ? mapOptionsWithId(option.options) : undefined,
    id: option.id ?? uniqueId(),
  }));

const ActionButton = React.memo<TProps>(
  ({
    disabled,
    options,
    appearance = 'primary',
    size = 'M',
    overlayPlacementX = 'right',
    overlayPlacementY = 'bottom',
    type = 'button',
    children,
    overlayClassName,
    ...props
  }) => {
    const optionsWithId = React.useMemo(() => mapOptionsWithId(options), [options]);
    const closeButtonRef = React.useRef<HTMLButtonElement>(null);

    return (
      <S.Dropdown
        head={
          <S.Button {...props} disabled={disabled} size={size} appearance={appearance} type={type}>
            <Stack size="1" direction="ROW" align="center">
              {children}
            </Stack>
          </S.Button>
        }
        disabled={disabled}
        overlay={
          <>
            <Options
              options={optionsWithId}
              size={size}
              placementX={overlayPlacementX}
              placementY={overlayPlacementY}
              overlayClassName={overlayClassName}
              onSelect={() => closeButtonRef.current?.click()}
            />
            <button type="button" name="cancel" hidden ref={closeButtonRef} />
          </>
        }
        offsetY={DEFAULT_OFFSET}
        placementX={overlayPlacementX}
        placementY={overlayPlacementY}
        overlayClassName={overlayClassName}
        closeWhen="clickOutsideOverlayOrSubmitOrCancel"
      />
    );
  },
);

export type {TActionButtonOption};
export {ActionButton};
/* eslint-enable react/jsx-props-no-spreading */
